<template>
  <div class="phone-resolver">
    <div class="box box--default phone-resolver__box">
      <Loading v-if="loading" />

      <template v-else-if="customers.length">
        <div class="phone-resolver__title">
          {{ $t("customerByPhoneResolver.multipleTitle") }}
        </div>
        <div class="phone-resolver__message">
          {{ $t("customerByPhoneResolver.selectMessage", { phone: decodedPhone }) }}
        </div>

        <button
          v-for="customer in customers"
          :key="customer.id"
          type="button"
          class="phone-resolver__customer"
          @click="openCustomer(customer.id)"
        >
          <span class="phone-resolver__name">{{ customerName(customer) }}</span>
          <span class="phone-resolver__team">
            {{ customer.team && customer.team.name ? customer.team.name : "" }}
          </span>
        </button>
      </template>

      <template v-else>
        <div class="phone-resolver__title">{{ title }}</div>
        <div class="phone-resolver__message">{{ message }}</div>

        <div class="phone-resolver__actions">
          <button type="button" class="button button--default" @click="$router.push('/customers')">
            {{ $t("customerByPhoneResolver.customersButton") }}
          </button>
          <button type="button" class="button button--default" @click="$router.push('/dashboard')">
            {{ $t("customerByPhoneResolver.dashboardButton") }}
          </button>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import api from "@/api";
import Loading from "@/partials/Loading";

export default {
  name: "CustomerByPhoneResolver",

  components: {
    Loading,
  },

  data() {
    return {
      loading: true,
      customers: [],
      titleKey: "resolvingTitle",
      messageKey: "resolvingMessage",
    };
  },

  computed: {
    decodedPhone() {
      return this.$route.params.phone || "";
    },

    title() {
      return this.$t(`customerByPhoneResolver.${this.titleKey}`);
    },

    message() {
      return this.$t(`customerByPhoneResolver.${this.messageKey}`);
    },
  },

  created() {
    this.resolveCustomer();
  },

  methods: {
    async resolveCustomer() {
      this.loading = true;
      this.customers = [];
      this.titleKey = "resolvingTitle";
      this.messageKey = "resolvingMessage";

      try {
        const response = await api({ requiresAuth: true }).get("/v1/customers/find-by-phone", {
          params: {
            phone: this.decodedPhone,
          },
        });

        if (response.data && response.data.customer_id) {
          this.openCustomer(response.data.customer_id);
          return;
        }

        this.showNotFound();
      } catch (error) {
        if (error.response && error.response.status === 409) {
          this.customers = this.extractCustomers(error.response.data.customers);
        } else if (error.response && error.response.status === 404) {
          this.showNotFound();
        } else {
          this.titleKey = "errorTitle";
          this.messageKey = "errorMessage";
        }
      } finally {
        this.loading = false;
      }
    },

    extractCustomers(customers) {
      if (Array.isArray(customers)) {
        return customers;
      }

      if (customers && Array.isArray(customers.data)) {
        return customers.data;
      }

      return [];
    },

    customerName(customer) {
      return customer.full_name
        || customer.company_name
        || `${customer.first_name || ""} ${customer.last_name || ""}`.trim()
        || this.$t("customerByPhoneResolver.customerFallback", { id: customer.id });
    },

    openCustomer(customerId) {
      this.$router.replace(`/account-details/customer/${customerId}`);
    },

    showNotFound() {
      this.titleKey = "notFoundTitle";
      this.messageKey = "notFoundMessage";
    },
  },
};
</script>

<style lang="scss" scoped>
.phone-resolver {
  min-height: 420px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px;

  &__box {
    width: 100%;
    max-width: 520px;
    min-height: 180px;
    padding: 28px;
  }

  &__title {
    font-size: 20px;
    font-weight: 700;
    color: #354052;
    margin-bottom: 10px;
  }

  &__message {
    font-size: 14px;
    color: #7f8fa4;
    margin-bottom: 20px;
  }

  &__customer {
    width: 100%;
    border: 1px solid rgba(127, 143, 164, 0.3);
    background: #fff;
    border-radius: 4px;
    padding: 14px 16px;
    margin-bottom: 10px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    cursor: pointer;
    text-align: left;
  }

  &__name {
    color: #354052;
    font-weight: 700;
  }

  &__team {
    color: #7f8fa4;
    font-size: 13px;
    margin-left: 12px;
  }

  &__actions {
    display: flex;
    gap: 12px;
  }
}
</style>
